import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';

export interface Account {
  id: string;
  type: string;
  name: string;
  bank: string | null;
  number: string | null;
  currency: string;
  openingBalance: string;
  balance: string;
  active: boolean;
}

export interface TreasuryMovement {
  id: string;
  accountId: string;
  type: string;
  amount: string;
  source: string;
  description: string | null;
  date: string;
}

function errMsg(e: unknown, fallback: string): string {
  return (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? fallback;
}

export function useAccounts() {
  return useQuery({
    queryKey: ['treasury-accounts'],
    queryFn: async () => (await apiClient.get<Account[]>('/treasury/accounts')).data,
    retry: false,
  });
}

export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { type: string; name: string; bank?: string; number?: string; openingBalance: number }) =>
      (await apiClient.post('/treasury/accounts', input)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['treasury-accounts'] });
      toast.success('Cuenta creada');
    },
    onError: (e: unknown) => toast.error(errMsg(e, 'No se pudo crear la cuenta')),
  });
}

export function useMovements(accountId?: string) {
  return useQuery({
    queryKey: ['treasury-movements', accountId ?? 'all'],
    queryFn: async () =>
      (await apiClient.get<TreasuryMovement[]>('/treasury/movements', { params: accountId ? { accountId } : {} })).data,
    retry: false,
  });
}

export function useCreateMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { accountId: string; type: 'IN' | 'OUT'; amount: number; description?: string }) =>
      (await apiClient.post('/treasury/movements', input)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['treasury-accounts'] });
      qc.invalidateQueries({ queryKey: ['treasury-movements'] });
      toast.success('Movimiento registrado');
    },
    onError: (e: unknown) => toast.error(errMsg(e, 'No se pudo registrar el movimiento')),
  });
}

export function useTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { fromAccountId: string; toAccountId: string; amount: number; description?: string }) =>
      (await apiClient.post('/treasury/transfer', input)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['treasury-accounts'] });
      qc.invalidateQueries({ queryKey: ['treasury-movements'] });
      toast.success('Transferencia realizada');
    },
    onError: (e: unknown) => toast.error(errMsg(e, 'No se pudo transferir')),
  });
}
